import { memo, useEffect, useState } from "react";
import { useUser } from "@clerk/react";
import { useStore } from "../store/globalState";
import { useTranslation } from "react-i18next";
import {
  Button,
  CashSpecialIcon,
  GradientText,
  Layout,
  SupportIcon,
  Title,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { BodyCopy } from "../components/BodyCopy";
import { FlowHeroMark } from "../components/FlowHeroMark";
import { authPagePath } from "../auth/resume";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { formatLabeledAmount } from "../utils";
import {
  requestedAmountCurrency,
  resolveAmountAnchor,
} from "../utils/amountAnchor";
import { useClaimLink } from "../hooks/useClaimLink";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useSessionStore } from "../store/useSessionStore";
import { useRequestPayableGuard } from "../hooks/useRequestPayableGuard";
import { getEvents, safeCapture } from "../analytics";
import { discardFundRequest, getWidgetApiError } from "../services/api";
import { RequestStatus } from "../utils/commons";
import { toTitleCase } from "../utils/toTitleCase";
import { ConsumerSessionMenu } from "../components/ConsumerSessionMenu";
import { getClerkConsumerPublishableKey } from "../auth/clerkPublishableKey";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { blurActiveTextInput } from "../utils/blurActiveTextInput";
import { Navbar } from "../components/Navbar";

/**
 * Entry screen of the fund-request flow.
 *
 * Shows an incoming money request: who requested it and the requested amount,
 * both resolved from the claimed link (`userData.name` + `config.amount`).
 * "Pay" continues to auth/transfer. "Later" keeps the same link payable.
 * "Dismiss" cancels the money request, then opens the discarded screen.
 * The cash icon sits on a white disc.
 */
interface FundRequestPageContentProps {
  isSignedIn?: boolean;
}

export const FundRequestPage = memo(() => {
  if (!getClerkConsumerPublishableKey()) {
    return <FundRequestPageContent />;
  }
  return <SignedInAwareFundRequestPage />;
});

const SignedInAwareFundRequestPage = memo(() => {
  const { isSignedIn } = useUser();
  return <FundRequestPageContent isSignedIn={isSignedIn} />;
});

const FundRequestPageContent = memo(({
  isSignedIn = false,
}: FundRequestPageContentProps) => {
  useClaimLink();
  useRequestPayableGuard();
  const { t } = useTranslation();
  const { session, updateSession } = useSessionStore();
  const [discarding, setDiscarding] = useState(false);
  const [discardError, setDiscardError] = useState(false);
  const { theme } = useStore();
  const { redirectTo } = useRouteWithToken();

  const colorConfig = theme.whiteView;
  const {
    background,
    backgroundBtn,
    textColorBtn,
    dropzoneColors,
    backgroundIcon,
  } = colorConfig;
  const userData = session.data?.userData;
  const rawRequesterName =
    [userData?.name?.firstName, userData?.name?.lastName]
      .filter(Boolean)
      .join(" ") || userData?.fullName;
  const requesterName = rawRequesterName
    ? toTitleCase(rawRequesterName)
    : t("fundRequest.someone");

  const sessionReady =
    session.status === RequestStatus.LOADED && Boolean(session.data);
  const config = session.data?.config;
  const requestedAmount = sessionReady ? (config?.amount ?? 0) : 0;
  const amountAnchor = resolveAmountAnchor(config);
  const requestedCurrency = requestedAmountCurrency(config, amountAnchor);
  const formattedAmount = formatLabeledAmount(
    requestedAmount,
    requestedCurrency
  );
  useEffect(() => {
    safeCapture(getEvents().FUND_REQUEST_ENTRY_VIEW);
  }, []);

  const handleLater = () => {
    if (!sessionReady) return;
    safeCapture(getEvents().FUND_REQUEST_LATER_CLICK);
    redirectTo(APP_ROUTES.LATER_PAGE);
  };

  const handleDiscard = async () => {
    if (!sessionReady || discarding) return;
    const linkId = session.data?.linkId;
    if (!linkId) return;
    setDiscarding(true);
    setDiscardError(false);
    safeCapture(getEvents().FUND_REQUEST_DISCARD_CLICK);
    try {
      await discardFundRequest(linkId);
      redirectTo(APP_ROUTES.DISCARD_PAGE, { state: { discarded: true } });
    } catch (error) {
      const apiError = getWidgetApiError(error);
      if (apiError?.code === "REQUEST_ALREADY_FULFILLED") {
        const details = apiError.details;
        const reported =
          details &&
          typeof details === "object" &&
          "status" in details &&
          typeof (details as { status?: unknown }).status === "string"
            ? (details as { status: string }).status
            : "cancelled";
        const requestStatus =
          reported === "accepted" ||
          reported === "funded" ||
          reported === "failed" ||
          reported === "expired" ||
          reported === "cancelled"
            ? reported
            : "cancelled";
        const current = useSessionStore.getState().session.data;
        if (current?.config) {
          updateSession({
            ...current,
            config: { ...current.config, requestStatus },
          });
        }
        redirectTo(APP_ROUTES.REQUEST_ALREADY_FULFILLED_PAGE, { replace: true });
        return;
      }
      setDiscardError(true);
      setDiscarding(false);
    }
  };

  const handlePay = () => {
    if (!sessionReady) return;
    safeCapture(getEvents().FUND_REQUEST_PAY_CLICK);
    redirectTo(authPagePath({ kind: "transfer" }, { fromClaim: true }));
  };

  const { openSupport, supportSheet } = useSupportSheet();

  return (
    <>
      <AuraLayout colorConfig={colorConfig} className="scrollable need-help-header">
        <Layout.Header>
          {isSignedIn && (
            <ConsumerSessionMenu
              resume={{ kind: "transfer" }}
              fromClaim
              analyticsScreen="fund_request"
            />
          )}
          <Navbar
            title={t("common.needHelp")}
            align="right"
            noBackButton
            onIconClick={() => openSupport()}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>
        <Layout.Content className="landing-section fund-request-section">
          <ScrollFadeIndicator color={colorConfig.bodyCopy} />
          <Title
            title={t("fundRequest.requestedYou", { name: requesterName })}
            align="left"
            color={TITLE_COLOR}
          />
          <section className="fund-request-hero">
            <div className="fund-request-disc">
              <FlowHeroMark
                dropzoneColors={dropzoneColors}
                backgroundIcon={backgroundIcon}
              >
                <CashSpecialIcon size={60} colors={dropzoneColors} />
              </FlowHeroMark>
            </div>

            <section className="landing-container fund-request-amount">
              <GradientText
                as="label"
                textColor={TITLE_COLOR}
                className="fund-request-label"
              >
                {t("fundRequest.requestedAmount")}
              </GradientText>
              <GradientText as="h1" textColor={TITLE_COLOR}>
                {formattedAmount}
              </GradientText>
            </section>
          </section>
        </Layout.Content>

        <Layout.Footer>
          <section className="footer-section">
            {discardError && (
              <BodyCopy textColor={TITLE_COLOR}>{t("fundRequest.discardScreen.failed")}</BodyCopy>
            )}
            <Button
              type="button"
              variant="cancel"
              size="small"
              color={TITLE_COLOR}
              className="fund-request-discard"
              disabled={!sessionReady || discarding}
              onClick={handleDiscard}
            >
              {t("fundRequest.discard")}
            </Button>

            <section className="fund-request-actions fund-request-actions--stack">
              <Button
                type="button"
                size="large"
                variant="outline"
                background={backgroundBtn}
                color={TITLE_COLOR}
                style={{ backgroundColor: background }}
                disabled={!sessionReady || discarding}
                onClick={handleLater}
              >
                {t("fundRequest.later")}
              </Button>
              <Button
                type="button"
                size="large"
                background={backgroundBtn}
                color={textColorBtn}
                disabled={!sessionReady || discarding}
                onPointerDown={blurActiveTextInput}
                onClick={handlePay}
              >
                {t("fundRequest.pay")}
              </Button>
            </section>
          </section>
        </Layout.Footer>
      </AuraLayout>
      {supportSheet}
    </>
  );
});
